import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { CheckCircle2, XCircle, Loader2, Award, Calendar, User, Hash, ExternalLink } from 'lucide-react'

interface CertificateResult {
  found: boolean
  id?: string
  certificate_type?: string
  title?: string
  issue_date?: string
  verification_code?: string
  student_name?: string
}

export default function VerifyCertificate() {
  const { code } = useParams()
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState<CertificateResult | null>(null)

  useEffect(() => {
    if (code) verify(code)
  }, [code])

  const verify = async (verificationCode: string) => {
    setLoading(true)
    const { data } = await (supabase.rpc as any)('verify_certificate', {
      p_verification_code: verificationCode
    })
    setResult(data as CertificateResult | null)
    setLoading(false)
  }

  const found = result?.found === true

  const issueDate = result?.issue_date
    ? new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(result.issue_date))
    : ''

  const linkedInUrl = found && result
    ? `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(result.title || 'Certificado Francés A1')}&organizationName=${encodeURIComponent('NomadLexis - Lengua Francesa')}&issueYear=${new Date(result.issue_date!).getFullYear()}&issueMonth=${new Date(result.issue_date!).getMonth() + 1}&certUrl=${encodeURIComponent(window.location.href)}&certId=${encodeURIComponent(result.verification_code!)}`
    : ''

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Nomad<span className="text-[#EF4135]">Lexis</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Verificacion de Certificado</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
            <Loader2 className="w-10 h-10 text-[#0055A4] animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Verificando certificado...</p>
          </div>
        ) : found && result ? (
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            {/* Valid banner */}
            <div className="bg-green-50 border-b border-green-200 p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-green-800">Certificado Válido</h2>
              <p className="text-sm text-green-600 mt-1">
                Este certificado fue emitido por NomadLexis y es auténtico
              </p>
            </div>

            {/* Certificate details */}
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Otorgado a</p>
                  <p className="text-lg font-semibold text-gray-900">{result.student_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Certificado</p>
                  <p className="font-medium text-gray-900">{result.title}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Fecha de emisión</p>
                  <p className="text-gray-900">{issueDate}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Hash className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Código de verificación</p>
                  <p className="font-mono text-sm text-gray-900">{result.verification_code}</p>
                </div>
              </div>
            </div>

            {/* LinkedIn button */}
            <div className="px-6 pb-6">
              <a
                href={linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-[#0077B5] text-white rounded-xl font-medium hover:bg-[#006399] transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                Agregar a LinkedIn
              </a>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            {/* Invalid banner */}
            <div className="bg-red-50 border-b border-red-200 p-6 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-red-800">Certificado No Encontrado</h2>
              <p className="text-sm text-red-600 mt-1">
                El código de verificación no corresponde a ningún certificado emitido
              </p>
            </div>
            <div className="p-6 text-center">
              <p className="text-sm text-gray-500 mb-1">Código ingresado:</p>
              <p className="font-mono text-lg text-gray-900">{code}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-[#0055A4] hover:underline flex items-center justify-center gap-1">
            <ExternalLink className="w-3.5 h-3.5" />
            Visitar NomadLexis
          </Link>
        </div>
      </div>
    </div>
  )
}
